import { useEffect, useState } from "react";
import axios from "axios";
import { BbotButton, BbotModal, Input, notification, Select, Space } from "top-component-library";
import { generalErrorAlert } from "util/Utils";
import { determineSwitchTextChoice, switchTextForChoice, toRawHostnameConfig } from "../utils";
import SwitchTextField from "./SwitchTextField";

// Mirrors enable-basic-delivery-theme-dialog.html/enableBasicDeliveryThemeController: a
// wizard that skips straight to setConfig unless the hostname is already
// basicDeliveryTheme, in which case it first asks whether to change settings or
// disable instead.
const EnableBasicDeliveryThemeDialog = ({ visible, hostnameConfig, onClose, onSave }) => {
  const [step, setStep] = useState("setConfig");
  const [customerPrettyName, setCustomerPrettyName] = useState("");
  const [switchTextChoice, setSwitchTextChoice] = useState("default");
  const [switchText, setSwitchText] = useState("Switch Location");
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const startStep = hostnameConfig.configType === "basicDeliveryTheme" ? "intro" : "setConfig";

  useEffect(() => {
    if (!visible) return;
    setStep(startStep);
    setCustomerPrettyName(hostnameConfig.customer_pretty_name ?? "");
    const choice = determineSwitchTextChoice(hostnameConfig.switch_text);
    setSwitchTextChoice(choice);
    setSwitchText(hostnameConfig.switch_text ?? switchTextForChoice(choice));
    setSelectedLocationId(hostnameConfig.redirect_to_delivery_location?.locationId ?? null);

    axios
      .get("/owner/getAllLocations")
      .then((res) => {
        setLocations([...res.data.locations].sort((a, b) => a.locationShortId.localeCompare(b.locationShortId)));
      })
      .catch((error) => generalErrorAlert(error, "Error retrieving locations, please try again"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, hostnameConfig.hostname]);

  const onSwitchTextChoiceChange = (choice) => {
    setSwitchTextChoice(choice);
    setSwitchText(switchTextForChoice(choice));
  };

  const onConfirm = async () => {
    if (step === "disableWarning") {
      notification.info({
        message:
          "We do not support disabling this configuration through the Owner Portal at this time. Please submit a support request and we'll handle it as soon as we can.",
        duration: 8,
      });
      return;
    }
    if (!selectedLocationId) {
      notification.info({ message: "Please select an option from the list of locations to continue" });
      return;
    }

    setIsSaving(true);
    const succeeded = await onSave(
      {
        ...toRawHostnameConfig(hostnameConfig),
        customer_pretty_name: customerPrettyName,
        switch_text: switchText,
      },
      selectedLocationId
    );
    setIsSaving(false);
    if (succeeded) onClose();
  };

  const isInvalid = step === "setConfig" && (!customerPrettyName.trim() || !selectedLocationId);

  return (
    <BbotModal
      title={
        {
          intro: "What would you like to do?",
          setConfig: "Menu-only BOS Settings",
          disableWarning: "Disable Menu-only BOS",
        }[step]
      }
      visible={visible}
      onCancel={onClose}
      footer={
        <Space>
          {startStep === "intro" && step !== "intro" && (
            <BbotButton onClick={() => setStep("intro")}>Start Over</BbotButton>
          )}
          <BbotButton onClick={onClose}>Close</BbotButton>
          {step !== "intro" && (
            <BbotButton type="primary" onClick={onConfirm} disabled={isInvalid} loading={isSaving}>
              Confirm
            </BbotButton>
          )}
        </Space>
      }
    >
      {step === "intro" && (
        <Space direction="vertical">
          <BbotButton type="primary" onClick={() => setStep("setConfig")}>
            Change my settings
          </BbotButton>
          <BbotButton onClick={() => setStep("disableWarning")}>Disable Menu-only BOS</BbotButton>
        </Space>
      )}

      {step === "setConfig" && (
        <div>
          <label htmlFor="basic-delivery-customer-pretty-name">
            Customer Display Name (shown in the tab title and on receipts)
          </label>
          <Input
            id="basic-delivery-customer-pretty-name"
            value={customerPrettyName}
            onChange={(e) => setCustomerPrettyName(e.target.value)}
          />
          <p className="margin-top-2">
            Please choose a table/location code to redirect the guest to when they navigate to this domain.
          </p>
          <p>
            <strong>
              <i>Note: The location code must already exist at this customer.</i>
            </strong>
          </p>
          <Select
            id="basic-delivery-location"
            showSearch
            placeholder="Start typing the location name..."
            value={selectedLocationId}
            onChange={setSelectedLocationId}
            filterOption={(input, option) => {
              const location = locations.find((loc) => loc.locationId === option.value);
              return Boolean(location?.locationShortId.toLowerCase().includes(input.toLowerCase().replace(/\s/g, "")));
            }}
            notFoundContent="No locations matching found."
          >
            {locations.map((location) => (
              <Select.Option key={location.locationId} value={location.locationId}>
                {location.locationName} ({location.locationShortId})
              </Select.Option>
            ))}
          </Select>
          <p className="margin-top-2">
            Please enter the text for the <strong>&quot;Switch Location&quot;</strong> button on the homepage, or leave
            it blank to not show the <strong>&quot;Switch Location&quot;</strong> button. (For example, you might set it
            to <strong>&quot;Switch Room&quot;</strong> if the customer is a hotel.)
          </p>
          <SwitchTextField
            choice={switchTextChoice}
            text={switchText}
            onChoiceChange={onSwitchTextChoiceChange}
            onTextChange={setSwitchText}
          />
        </div>
      )}

      {step === "disableWarning" && (
        <div>
          <p>
            Continuing will <strong>completely</strong> disable this feature and remove your saved settings. Guests
            visiting {hostnameConfig.hostname} will be shown the bbot.menu homepage and you will have to put in a
            support request for a new Branded Ordering Site if the BOS Builder is not available yet. If you change your
            mind, you can always re-enable the feature on the settings page.
          </p>
          <p>
            Press &apos;<strong>confirm</strong>&apos; to disable the config, or start over to go back.
          </p>
        </div>
      )}
    </BbotModal>
  );
};

export default EnableBasicDeliveryThemeDialog;
